import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Button,
    TextField,
    IconButton,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ListItemText,
    ListItemIcon,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Menu,
    Breadcrumbs,
    Link
} from '@mui/material';
// CodeMirror imports removed - use TextField instead

// icons
import {
    IconNotebook,
    IconPlus,
    IconUpload,
    IconDownload,
    IconTrash,
    IconEdit,
    IconCopy,
    IconEye,
    IconShare,
    IconPlayerPlay,
    IconRefresh,
    IconSearch,
    IconGitBranch,
    IconCode,
    IconDots,
    IconLayoutGrid,
    IconLayoutList
} from '@tabler/icons-react';

const NotebookManager = () => {
    const theme = useTheme();
    const [viewMode, setViewMode] = useState('grid');
    const [selectedNotebook, setSelectedNotebook] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterBy, setFilterBy] = useState('all');
    const [sortBy, setSortBy] = useState('modified');
    const [createDialog, setCreateDialog] = useState(false);
    const [previewDialog, setPreviewDialog] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState('root');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [anchorEl, setAnchorEl] = useState(null);

    // Mock notebooks data
    const notebooks = [
        {
            id: 1,
            name: 'Data Analysis Tutorial.ipynb',
            path: '/home/jovyan/work/tutorials/',
            size: '2.4 MB',
            modified: '2 hours ago',
            created: '2024-01-10',
            kernel: 'Python 3',
            status: 'running',
            cells: 45,
            outputs: 23,
            tags: ['tutorial', 'pandas', 'visualization'],
            lastCheckpoint: '30 mins ago'
        },
        {
            id: 2,
            name: 'Machine Learning Model.ipynb',
            path: '/home/jovyan/work/ml/',
            size: '8.7 MB',
            modified: '5 mins ago',
            created: '2024-01-12',
            kernel: 'Python 3 (PyTorch)',
            status: 'running',
            cells: 128,
            outputs: 67,
            tags: ['ml', 'pytorch', 'deep-learning'],
            lastCheckpoint: '5 mins ago'
        },
        {
            id: 3,
            name: 'API Integration Example.ipynb',
            path: '/home/jovyan/work/examples/',
            size: '1.2 MB',
            modified: 'Yesterday',
            created: '2024-01-14',
            kernel: 'Python 3',
            status: 'idle',
            cells: 23,
            outputs: 15,
            tags: ['api', 'requests', 'example'],
            lastCheckpoint: 'Yesterday'
        },
        {
            id: 4,
            name: 'Database Queries.ipynb',
            path: '/home/jovyan/work/database/',
            size: '845 KB',
            modified: '3 days ago',
            created: '2024-01-08',
            kernel: 'Python 3',
            status: 'idle',
            cells: 18,
            outputs: 12,
            tags: ['database', 'sql', 'mongodb'],
            lastCheckpoint: '3 days ago'
        }
    ];

    // Mock folder structure
    const folders = [
        { name: 'work', path: '/home/jovyan/work/', count: 4 },
        { name: 'tutorials', path: '/home/jovyan/work/tutorials/', count: 2 },
        { name: 'ml', path: '/home/jovyan/work/ml/', count: 1 },
        { name: 'examples', path: '/home/jovyan/work/examples/', count: 1 }
    ];

    // Sample notebook preview
    const notebookPreview = {
        cells: [
            {
                type: 'markdown',
                content: '# Data Analysis Tutorial\n\nThis notebook demonstrates basic data analysis with pandas and visualization.'
            },
            {
                type: 'code',
                content:
                    'import pandas as pd\nimport numpy as np\nimport matplotlib.pyplot as plt\n\n# Load data\ndf = pd.read_csv("data.csv")\ndf.head()',
                output: {
                    type: 'dataframe',
                    data: [
                        { id: 1, name: 'John', age: 25, city: 'New York' },
                        { id: 2, name: 'Jane', age: 30, city: 'London' },
                        { id: 3, name: 'Bob', age: 35, city: 'Paris' }
                    ]
                }
            },
            {
                type: 'code',
                content:
                    '# Create visualization\nplt.figure(figsize=(10, 6))\ndf.groupby("city").size().plot(kind="bar")\nplt.title("Users by City")\nplt.show()',
                output: {
                    type: 'plot',
                    description: 'Bar chart showing user distribution by city'
                }
            }
        ]
    };

    const handleCreateNotebook = () => {
        setCreateDialog(true);
    };

    const handlePreviewNotebook = (notebook) => {
        setSelectedNotebook(notebook);
        setPreviewDialog(true);
    };

    const handleMenuClick = (event, notebook) => {
        setAnchorEl(event.currentTarget);
        setSelectedNotebook(notebook);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const filteredNotebooks = notebooks.filter((notebook) => {
        const matchesSearch =
            notebook.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            notebook.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesFilter =
            filterBy === 'all' ||
            (filterBy === 'running' && notebook.status === 'running') ||
            (filterBy === 'idle' && notebook.status === 'idle');
        return matchesSearch && matchesFilter;
    });

    const sortedNotebooks = [...filteredNotebooks].sort((a, b) => {
        switch (sortBy) {
            case 'name':
                return a.name.localeCompare(b.name);
            case 'modified':
                return new Date(b.modified) - new Date(a.modified);
            case 'size':
                return parseFloat(b.size) - parseFloat(a.size);
            default:
                return 0;
        }
    });

    return (
        <Box>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        Notebook Manager
                    </Typography>
                    <Breadcrumbs>
                        <Link color="inherit" href="#">
                            Home
                        </Link>
                        <Link color="inherit" href="#">
                            work
                        </Link>
                        <Typography color="text.primary">All Notebooks</Typography>
                    </Breadcrumbs>
                </Box>
                <Box display="flex" gap={1}>
                    <Button variant="outlined" startIcon={<IconUpload />} size="small">
                        Upload
                    </Button>
                    <Button variant="contained" startIcon={<IconPlus />} onClick={handleCreateNotebook}>
                        New Notebook
                    </Button>
                </Box>
            </Box>

            {/* Filters and Search */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                placeholder="Search notebooks..."
                                size="small"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconSearch size={18} />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Filter by</InputLabel>
                                <Select value={filterBy} label="Filter by" onChange={(e) => setFilterBy(e.target.value)}>
                                    <MenuItem value="all">All Notebooks</MenuItem>
                                    <MenuItem value="running">Running</MenuItem>
                                    <MenuItem value="idle">Idle</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Sort by</InputLabel>
                                <Select value={sortBy} label="Sort by" onChange={(e) => setSortBy(e.target.value)}>
                                    <MenuItem value="modified">Last Modified</MenuItem>
                                    <MenuItem value="name">Name</MenuItem>
                                    <MenuItem value="size">Size</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box display="flex" justifyContent="flex-end" gap={1}>
                                <ToggleButtonGroup
                                    value={viewMode}
                                    exclusive
                                    onChange={(e, newMode) => newMode && setViewMode(newMode)}
                                    size="small"
                                >
                                    <ToggleButton value="grid">
                                        <IconLayoutGrid size={18} />
                                    </ToggleButton>
                                    <ToggleButton value="list">
                                        <IconLayoutList size={18} />
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                <IconButton size="small">
                                    <IconRefresh />
                                </IconButton>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Notebooks Grid/List View */}
            {viewMode === 'grid' ? (
                <Grid container spacing={3}>
                    {sortedNotebooks.map((notebook) => (
                        <Grid item xs={12} md={6} lg={4} key={notebook.id}>
                            <Card
                                sx={{
                                    height: '100%',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: theme.shadows[8],
                                        transition: 'all 0.3s ease-in-out'
                                    }
                                }}
                            >
                                <CardContent>
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                        <Box>
                                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                <IconNotebook size={20} />
                                                <Typography variant="h6" noWrap sx={{ maxWidth: 200 }}>
                                                    {notebook.name}
                                                </Typography>
                                            </Box>
                                            <Typography variant="caption" color="textSecondary" display="block">
                                                {notebook.path}
                                            </Typography>
                                        </Box>
                                        <IconButton size="small" onClick={(e) => handleMenuClick(e, notebook)}>
                                            <IconDots />
                                        </IconButton>
                                    </Box>

                                    <Box display="flex" gap={0.5} my={2}>
                                        {notebook.tags.slice(0, 3).map((tag, index) => (
                                            <Chip key={index} label={tag} size="small" />
                                        ))}
                                        {notebook.tags.length > 3 && <Chip label={`+${notebook.tags.length - 3}`} size="small" />}
                                    </Box>

                                    <Divider sx={{ my: 2 }} />

                                    <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="textSecondary">
                                                Kernel
                                            </Typography>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        bgcolor:
                                                            notebook.status === 'running'
                                                                ? theme.palette.success.main
                                                                : theme.palette.grey[400]
                                                    }}
                                                />
                                                <Typography variant="body2">{notebook.kernel}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="textSecondary">
                                                Size
                                            </Typography>
                                            <Typography variant="body2">{notebook.size}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="textSecondary">
                                                Cells
                                            </Typography>
                                            <Typography variant="body2">
                                                {notebook.cells} ({notebook.outputs} outputs)
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="textSecondary">
                                                Modified
                                            </Typography>
                                            <Typography variant="body2">{notebook.modified}</Typography>
                                        </Grid>
                                    </Grid>

                                    <Box display="flex" gap={1} mt={2}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<IconPlayerPlay />}
                                            fullWidth
                                            disabled={notebook.status === 'running'}
                                        >
                                            Open
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<IconEye />}
                                            onClick={() => handlePreviewNotebook(notebook)}
                                        >
                                            Preview
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Path</TableCell>
                                <TableCell>Kernel</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Size</TableCell>
                                <TableCell>Modified</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedNotebooks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((notebook) => (
                                <TableRow key={notebook.id} hover>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <IconNotebook size={18} />
                                            <Typography variant="body2">{notebook.name}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">{notebook.path}</Typography>
                                    </TableCell>
                                    <TableCell>{notebook.kernel}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={notebook.status}
                                            size="small"
                                            color={notebook.status === 'running' ? 'success' : 'default'}
                                            icon={
                                                <Box
                                                    sx={{
                                                        width: 6,
                                                        height: 6,
                                                        borderRadius: '50%',
                                                        bgcolor:
                                                            notebook.status === 'running'
                                                                ? theme.palette.success.main
                                                                : theme.palette.grey[400]
                                                    }}
                                                />
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>{notebook.size}</TableCell>
                                    <TableCell>{notebook.modified}</TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small">
                                            <IconPlayerPlay size={16} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handlePreviewNotebook(notebook)}>
                                            <IconEye size={16} />
                                        </IconButton>
                                        <IconButton size="small">
                                            <IconDownload size={16} />
                                        </IconButton>
                                        <IconButton size="small" color="error">
                                            <IconTrash size={16} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={sortedNotebooks.length}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                    />
                </TableContainer>
            )}

            {/* Notebook Menu */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleMenuClose}>
                    <ListItemIcon>
                        <IconEdit size={18} />
                    </ListItemIcon>
                    <ListItemText>Rename</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleMenuClose}>
                    <ListItemIcon>
                        <IconCopy size={18} />
                    </ListItemIcon>
                    <ListItemText>Duplicate</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleMenuClose}>
                    <ListItemIcon>
                        <IconDownload size={18} />
                    </ListItemIcon>
                    <ListItemText>Download</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleMenuClose}>
                    <ListItemIcon>
                        <IconShare size={18} />
                    </ListItemIcon>
                    <ListItemText>Share</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleMenuClose}>
                    <ListItemIcon>
                        <IconGitBranch size={18} />
                    </ListItemIcon>
                    <ListItemText>Version History</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
                    <ListItemIcon>
                        <IconTrash size={18} color={theme.palette.error.main} />
                    </ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                </MenuItem>
            </Menu>

            {/* Create Notebook Dialog */}
            <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create New Notebook</DialogTitle>
                <DialogContent>
                    <TextField fullWidth label="Notebook Name" placeholder="My Notebook.ipynb" sx={{ mt: 2, mb: 2 }} />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Kernel</InputLabel>
                        <Select defaultValue="python3" label="Kernel">
                            <MenuItem value="python3">Python 3</MenuItem>
                            <MenuItem value="python3-pytorch">Python 3 (PyTorch)</MenuItem>
                            <MenuItem value="python3-tensorflow">Python 3 (TensorFlow)</MenuItem>
                            <MenuItem value="r">R</MenuItem>
                            <MenuItem value="julia">Julia</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth>
                        <InputLabel>Folder</InputLabel>
                        <Select defaultValue="/home/jovyan/work/" label="Folder">
                            {folders.map((folder) => (
                                <MenuItem key={folder.path} value={folder.path}>
                                    {folder.path}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => setCreateDialog(false)}>
                        Create Notebook
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Preview Dialog */}
            <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                            <IconNotebook />
                            {selectedNotebook?.name}
                        </Box>
                        <Box display="flex" gap={1}>
                            <Chip label={selectedNotebook?.kernel} size="small" icon={<IconCode size={14} />} />
                            <Chip label={`${selectedNotebook?.cells} cells`} size="small" />
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {notebookPreview.cells.map((cell, index) => (
                        <Box key={index} mb={2}>
                            {cell.type === 'markdown' ? (
                                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                    <Typography variant="body1" component="div">
                                        {cell.content.split('\n').map((line, i) => (
                                            <div key={i}>{line}</div>
                                        ))}
                                    </Typography>
                                </Paper>
                            ) : (
                                <Box>
                                    <Paper sx={{ bgcolor: 'grey.100' }}>
                                        <Box sx={{ p: 1, bgcolor: 'grey.200' }}>
                                            <Typography variant="caption" color="textSecondary">
                                                In [{index}]:
                                            </Typography>
                                        </Box>
                                        <Box sx={{ p: 2 }}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                value={cell.content}
                                                InputProps={{
                                                    readOnly: true,
                                                    sx: {
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.875rem',
                                                        bgcolor: 'transparent'
                                                    }
                                                }}
                                                variant="standard"
                                            />
                                        </Box>
                                    </Paper>
                                    {cell.output && (
                                        <Paper sx={{ mt: 1, p: 2 }}>
                                            <Typography variant="caption" color="textSecondary">
                                                Out [{index}]:
                                            </Typography>
                                            {cell.output.type === 'dataframe' ? (
                                                <TableContainer sx={{ mt: 1 }}>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                {Object.keys(cell.output.data[0]).map((key) => (
                                                                    <TableCell key={key}>{key}</TableCell>
                                                                ))}
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {cell.output.data.map((row, i) => (
                                                                <TableRow key={i}>
                                                                    {Object.values(row).map((value, j) => (
                                                                        <TableCell key={j}>{value}</TableCell>
                                                                    ))}
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            ) : (
                                                <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                                                    <Typography variant="body2">📊 {cell.output.description}</Typography>
                                                </Box>
                                            )}
                                        </Paper>
                                    )}
                                </Box>
                            )}
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button startIcon={<IconPlayerPlay />} variant="contained">
                        Open in Jupyter
                    </Button>
                    <Button onClick={() => setPreviewDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default NotebookManager;
